import { createPortal } from "react-dom"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { X, ShieldAlert, Inbox } from "lucide-react"
import { toast } from "@/components/portal/Toast"

/**
 * Der echte IMAP-Spam-Ordner, live vom Mailserver (#55, Paul 27.08.).
 *
 * Spam wird bewusst nicht in die Datenbank gesynct — die Ansicht liest die
 * Header direkt vom Server. Einzige Aktion: eine faelschlich einsortierte
 * Mail in den Posteingang retten; der naechste Sync holt sie dann normal ab.
 *
 * 🔴 Modal per createPortal (stehende Regel) — und ein Ladefehler wird als
 * Fehler gezeigt, nie als „kein Spam" (stumme Nulltreffer).
 */

type SpamMail = { uid: string; subject: string; sender: string; date: string }
type SpamData = { ok: boolean; folder: string | null; mails: SpamMail[]; error?: string }

export function SpamModal({ tenant, postfach, onClose }: { tenant: string; postfach?: string; onClose: () => void }) {
  const qc = useQueryClient()
  const [rettend, setRettend] = useState<string | null>(null)

  const q = useQuery({
    queryKey: ["spam-folder", tenant, postfach || ""],
    queryFn: async (): Promise<SpamData> => {
      // Spam liegt je Postfach; ohne Auswahl das Hauptpostfach.
      const r = await fetch(`/api/v1/folders/spam?tenant=${encodeURIComponent(tenant)}${postfach ? `&postfach=${encodeURIComponent(postfach)}` : ""}`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    staleTime: 1000 * 60,
  })

  const retten = async (uid: string, sender: string) => {
    setRettend(uid)
    try {
      const r = await fetch("/api/v1/folders/spam/rescue", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        // Absender mitgeben: „Kein Spam" lernt (16.10.2026) — die Automatik
        // fasst ihn danach nicht mehr an.
        body: JSON.stringify({ tenant, uid, postfach: postfach || null, sender }),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`)
      toast(d.absender_gemerkt
        ? "✓ In den Posteingang verschoben — Absender wird nie mehr als Spam einsortiert"
        : "✓ In den Posteingang verschoben — kommt mit dem nächsten Sync")
      qc.invalidateQueries({ queryKey: ["spam-folder", tenant, postfach || ""] })
    } catch (e) {
      toast("Retten fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error")
    } finally {
      setRettend(null)
    }
  }

  const data = q.data

  return createPortal(
    <div
      className="pp-overlay fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-background border rounded-lg shadow-xl w-[min(720px,92vw)] max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="flex items-center gap-2 font-semibold text-[14px]">
            <ShieldAlert className="w-4 h-4 text-amber-600" />
            Spam-Ordner{data?.folder ? ` · ${data.folder}` : ""} <span className="text-muted-foreground font-normal">(live vom Mailserver)</span>
          </div>
          <button onClick={onClose} aria-label="Schließen" className="p-1 hover:bg-muted rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="overflow-y-auto p-2">
          {q.isLoading && <div className="p-6 text-center text-muted-foreground text-[13px]">Lade Spam-Ordner…</div>}
          {(q.isError || (data && !data.ok)) && (
            <div className="p-6 text-center text-[13px] text-red-600">
              Spam-Ordner konnte nicht geladen werden{data?.error ? `: ${data.error}` : ""} — das heißt nicht, dass er leer ist.
            </div>
          )}
          {data?.ok && data.folder === null && (
            <div className="p-6 text-center text-muted-foreground text-[13px]">
              Dieses Postfach hat keinen Spam-/Junk-Ordner.
            </div>
          )}
          {data?.ok && data.folder !== null && data.mails.length === 0 && (
            <div className="p-6 text-center text-muted-foreground text-[13px]">Der Spam-Ordner ist leer.</div>
          )}
          {data?.ok && data.mails.map((m) => (
            <div key={m.uid} className="flex items-center gap-3 px-3 py-2 border-b last:border-b-0 text-[12.5px]">
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{m.subject || "(kein Betreff)"}</div>
                <div className="text-muted-foreground truncate">{m.sender} · {m.date}</div>
              </div>
              <button
                className="inline-flex items-center gap-1 shrink-0 rounded border px-2 py-1 text-[12px] hover:bg-muted disabled:opacity-50"
                disabled={rettend === m.uid}
                onClick={() => retten(m.uid, m.sender)}
                title="Kein Spam — in den Posteingang verschieben"
              >
                <Inbox className="w-3.5 h-3.5" />
                {rettend === m.uid ? "Verschiebe…" : "Kein Spam"}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  )
}
